<template>
  <div :class="$style.topbar">
    <div class="mr-auto d-flex align-items-center">
      <div :class="$style.logo" class="mr-3">
        <router-link to="/">
          <img width="150" src="../../assets/img/logo-perki.png" />
        </router-link>
      </div>
      <a-menu
        v-model:selectedKeys="selectedKeys"
        mode="horizontal"
        :class="$style.navigation"
        @click="handleClick"
      >
        <template v-for="item in menuData" :key="item.key">
          <a-menu-item v-if="!item.children" :key="item.key">
            <router-link :to="item.url">
              <i :class="item.icon" class="mr-2"></i>
              {{ item.title }}
            </router-link>
          </a-menu-item>
          <a-sub-menu v-else :key="item.key + '_sub'" :title="item.title">
            <a-menu-item v-for="child in item.children" :key="child.key">
              <router-link :to="child.url">
                {{ child.title }}
              </router-link>
            </a-menu-item>
          </a-sub-menu>
        </template>
      </a-menu>
    </div>
    <div class="mr-4 mt-3 d-sm-block d-none">
      <p>
        Hello, <strong class="ml-1">{{ user.fullName ? user.fullName : 'Anonymous' }}</strong> as
        <strong>{{ user.role ? user.role : 'Guest' }}</strong>
      </p>
    </div>
    <!-- Notification Bell -->
    <div class="mr-3">
      <a-dropdown :trigger="['click']" placement="bottomRight" @visibleChange="onDropdownVisible">
        <a-badge :count="unreadCount" :overflow-count="99">
          <a-button type="text" class="notification-btn">
            <BellOutlined style="font-size: 20px" />
          </a-button>
        </a-badge>
        <template #overlay>
          <div class="notification-dropdown">
            <div class="notification-header">
              <span class="notification-title">Notifikasi</span>
              <a-button v-if="unreadCount > 0" type="link" size="small" @click.stop="markAllRead">
                Tandai semua dibaca
              </a-button>
            </div>
            <div class="notification-list">
              <a-spin v-if="loadingNotif" class="notification-loading" />
              <template v-else-if="notifications.length > 0">
                <div
                  v-for="notif in notifications"
                  :key="notif.id"
                  class="notification-item"
                  :class="{ unread: !notif.isRead }"
                  @click="handleNotificationClick(notif)"
                >
                  <div class="notif-icon">
                    <BellFilled v-if="!notif.isRead" style="color: #1890ff" />
                    <BellOutlined v-else style="color: #999" />
                  </div>
                  <div class="notif-content">
                    <div class="notif-title">{{ notif.title }}</div>
                    <div class="notif-desc">{{ notif.description }}</div>
                    <div class="notif-time">{{ formatTime(notif.createdAt) }}</div>
                  </div>
                </div>
              </template>
              <div v-else class="notification-empty">
                <InboxOutlined style="font-size: 32px; color: #999" />
                <p>Tidak ada notifikasi</p>
              </div>
            </div>
          </div>
        </template>
      </a-dropdown>
    </div>
    <div class="float-right">
      <vb-user-menu />
    </div>
  </div>
</template>

<script>
import VbUserMenu from './UserMenu'
import { useStore } from 'vuex'
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getMenuData } from '@/services/menu'
import { notificationAPI } from '@/services/axios/perki'
import { BellOutlined, BellFilled, InboxOutlined } from '@ant-design/icons-vue'
import moment from 'moment'

export default {
  components: {
    VbUserMenu,
    BellOutlined,
    BellFilled,
    InboxOutlined,
  },
  setup() {
    const store = useStore()
    const route = useRoute()
    const router = useRouter()
    const user = computed(() => store.getters['user/user'])
    const selectedKeys = ref([route.name])

    const menuData = getMenuData

    // Notification state
    const notifications = ref([])
    const unreadCount = ref(0)
    const loadingNotif = ref(false)
    let refreshInterval = null

    const fetchNotifications = async () => {
      try {
        loadingNotif.value = true
        const response = await notificationAPI.getAll({ page: 1, pageSize: 10 })
        if (response.data?.status === 'success') {
          notifications.value = response.data.data.notifications || []
          unreadCount.value = response.data.data.unreadCount || 0
        }
      } catch (error) {
        console.error('Error fetching notifications:', error)
      } finally {
        loadingNotif.value = false
      }
    }

    const onDropdownVisible = visible => {
      if (visible) {
        fetchNotifications()
      }
    }

    const handleNotificationClick = async notif => {
      // Mark as read if unread
      if (!notif.isRead) {
        try {
          await notificationAPI.markAsRead(notif.id)
          notif.isRead = true
          unreadCount.value = Math.max(0, unreadCount.value - 1)
        } catch (error) {
          console.error('Error marking notification as read:', error)
        }
      }

      // Navigate to the path
      if (notif.path) {
        router.push(notif.path)
      }
    }

    const markAllRead = async () => {
      try {
        await notificationAPI.markAllAsRead()
        notifications.value.forEach(n => (n.isRead = true))
        unreadCount.value = 0
      } catch (error) {
        console.error('Error marking all as read:', error)
      }
    }

    const formatTime = dateString => {
      if (!dateString) return ''
      const date = moment(dateString)
      const now = moment()

      if (now.diff(date, 'minutes') < 60) {
        return `${now.diff(date, 'minutes')} menit yang lalu`
      } else if (now.diff(date, 'hours') < 24) {
        return `${now.diff(date, 'hours')} jam yang lalu`
      } else if (now.diff(date, 'days') < 7) {
        return `${now.diff(date, 'days')} hari yang lalu`
      } else {
        return date.format('DD MMM YYYY HH:mm')
      }
    }

    const handleClick = e => {
      const item =
        menuData.find(m => m.key === e.key) ||
        menuData.flatMap(m => m.children || []).find(c => c.key === e.key)
      if (item && item.url) {
        router.push(item.url)
      }
    }

    onMounted(() => {
      // Fetch initial notifications
      fetchNotifications()

      // Refresh notifications every 30 seconds
      refreshInterval = setInterval(() => {
        fetchNotifications()
      }, 30000)
    })

    onUnmounted(() => {
      if (refreshInterval) {
        clearInterval(refreshInterval)
      }
    })

    return {
      user,
      selectedKeys,
      menuData,
      handleClick,
      // Notification
      notifications,
      unreadCount,
      loadingNotif,
      onDropdownVisible,
      handleNotificationClick,
      markAllRead,
      formatTime,
    }
  },
}
</script>

<style lang="scss" module>
@import './style.module.scss';
</style>

<style scoped>
.notification-btn {
  padding: 4px 8px;
  height: auto;
}

.notification-dropdown {
  background: white;
  border-radius: 8px;
  box-shadow: 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08),
    0 9px 28px 8px rgba(0, 0, 0, 0.05);
  width: 360px;
  max-height: 480px;
  overflow: hidden;
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
}

.notification-title {
  font-weight: 600;
  font-size: 16px;
  color: #262626;
}

.notification-list {
  max-height: 400px;
  overflow-y: auto;
}

.notification-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 0;
}

.notification-item {
  display: flex;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.2s;
  border-bottom: 1px solid #f5f5f5;
}

.notification-item:hover {
  background: #f5f5f5;
}

.notification-item.unread {
  background: #e6f7ff;
}

.notification-item.unread:hover {
  background: #d6efff;
}

.notif-icon {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
}

.notif-content {
  flex: 1;
  min-width: 0;
}

.notif-title {
  font-weight: 600;
  font-size: 14px;
  color: #262626;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.notif-desc {
  font-size: 13px;
  color: #595959;
  margin-bottom: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.notif-time {
  font-size: 12px;
  color: #8c8c8c;
}

.notification-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #999;
}

.notification-empty p {
  margin-top: 12px;
  margin-bottom: 0;
}
</style>
